/** Surfaces and level layout: `body.surface` (the static movement module of
 * every collision surface archetype), `world.layout` (places the authored
 * level) and `world.wrapX`. The layout rectangle format and its placement are
 * shared with `flow.director` (per-phase layouts). */
import type { JsonObject } from '../../json.js';
import type { ConfigSchema } from '../../schema.js';
import { configNamesKind, defineModule, type ModuleCtx } from '../module.js';
import { bool, id, list, num, obj, oneOf } from '../schemaBuilders.js';
import { addTag, type EntityV1 } from '../world.js';

/** A designer-friendly top-left rectangle naming the archetype placed there. */
export type LayoutRect = { archetype: string; x: number; y: number; w: number; h: number; dir?: number;
  /** What a placed tool/station IS (`params.item`): one neutral kind serves many items. */
  item?: string;
  /** Its on-screen name and palette colour (the presentation overlay). */
  label?: string; tint?: number };

export const layoutRectSchema: ConfigSchema = obj({ archetype: id(), x: num(-64, 128), y: num(-64, 128), w: num(0.1, 128), h: num(0.05, 16),
  dir: { type: 'number', min: -1, max: 1, integer: true }, item: id(), label: { type: 'string', maxLength: 24 },
  tint: { type: 'number', min: 0, max: 15, integer: true } }, ['dir', 'item', 'label', 'tint']);

/** Tint indices a layout uses must index the palette. */
export function layoutTintErrors(rects: readonly LayoutRect[], paletteSize: number, where: string): string[] {
  return rects.flatMap((s, i) => (s.tint !== undefined && s.tint >= paletteSize ? [`${where}[${i}]: tint ${s.tint} is not a palette index`] : []));
}

/** Place one rectangle, converted to the composed centre/bottom convention.
 * Sized kinds (surfaces, static bodies) take the rectangle as their size;
 * other kinds keep their archetype size and stand on the rectangle's bottom
 * edge (`dir` seeds a walker's heading and every kind's facing; `item`,
 * `label` and `tint` name and colour a placed tool). */
export function placeRect(ctx: ModuleCtx<unknown>, s: LayoutRect): EntityV1 {
  return ctx.spawn(s.archetype, { x: s.x + s.w / 2, y: s.y + s.h, facing: s.dir === -1 ? -1 : 1,
    params: { w: s.w, h: s.h, dir: s.dir ?? 1, ...(s.item !== undefined ? { item: s.item } : {}) },
    ...(s.label !== undefined ? { label: s.label } : {}), ...(s.tint !== undefined ? { tint: s.tint } : {}) });
}

/** A sized kind's `init`: adopt the placing rectangle's size when given. */
export function takeLayoutSize(e: EntityV1, params: JsonObject): void {
  if (typeof params.w === 'number') e.w = params.w;
  if (typeof params.h === 'number') e.h = params.h;
}

export type SurfaceBodyConfig = { mode: 'solid' | 'oneWay'; bumpable: boolean };

export const surfaceBody = defineModule<SurfaceBodyConfig>({
  id: 'body.surface', version: 1, attach: 'entity', role: 'movement',
  summary: 'A static collision surface: solid (blocks every side) or one-way (lands from above, passes from below); optionally bumpable from below.',
  config: obj({ mode: oneOf('solid', 'oneWay'), bumpable: bool() }),
  requires: [], events: [], presentation: {}, proofs: ['static-under-load', 'instance-size-from-layout'],
  animStates: () => [],
  init(_ctx, e, params) {
    addTag(e, 'surface');
    takeLayoutSize(e, params);
  },
  systems: {},
});

export type LayoutConfig = { layout: LayoutRect[] };

/** Places the authored level at match start from layout rectangles. */
export const worldLayout = defineModule<LayoutConfig>({
  id: 'world.layout', version: 1, attach: 'global', role: 'world',
  summary: 'Authored level layout: surfaces and placed entities from rectangles, spawned at match start in order.',
  config: obj({ layout: list(layoutRectSchema, 64, 1) }),
  requires: [], events: [], presentation: {}, proofs: ['layout-spawns-in-order', 'surface-takes-rect-size', 'placed-entity-stands-on-rect', 'placed-item-named-and-coloured'],
  animStates: () => [],
  mapArchetypeRefs: (c, rename) => ({ layout: c.layout.map(s => ({ ...s, archetype: rename(s.archetype) })) }),
  check: (c, context) => layoutTintErrors(c.layout, context.paletteSize, 'layout'),
  // Everything is placed at match start; a kind named elsewhere in an entry
  // (a placed row's params) is handed on to that entity's modules.
  placementsAhead: (ctx, kinds) => (ctx.config.layout.some(({ archetype: _placed, ...rest }) => configNamesKind(rest, kinds)) ? null : 0),
  initGlobal(ctx) {
    for (const s of ctx.config.layout) placeRect(ctx, s);
  },
  systems: {},
});

/** Horizontal wrap: running off one edge re-enters at the other. Movers call
 * `confineX`; this module is what turns the world's wrap on. */
export const worldWrapX = defineModule<Record<string, never>>({
  id: 'world.wrapX', version: 1, attach: 'global', role: 'world',
  summary: 'Wrap-around horizontal edges for every mover; overlap tests use the shortest wrapped distance.',
  config: obj({}),
  requires: [], events: [], presentation: {}, proofs: ['wrap-left-to-right', 'wrapped-overlap'],
  animStates: () => [],
  initGlobal(ctx) { ctx.world.bounds.wrapX = true; },
  systems: {},
});
